import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { NUMBER_RENDERED_PANELS_LIMIT } from '@experiment-management-shared/constants';
import { PanelsLayout } from '@experiment-management-shared/types';
import { initializeLayout } from '@experiment-management-shared/utils/layout';
import { GeneralChartContainerProps } from '@experiment-management-shared/components/Charts/Chart/ChartContainer/ChartContainer';
import isEmpty from 'lodash/isEmpty';
import useDeepCompareEffect from 'use-deep-compare-effect';

interface UseBatchChartContainerInfiniteScrollProps {
  isSearchMode: boolean;
  layout: PanelsLayout;
  configs: GeneralChartContainerProps[];
  entireRowCharts: boolean;
}

const useBatchChartContainerInfiniteScroll = ({
  isSearchMode,
  layout,
  configs,
  entireRowCharts
}: UseBatchChartContainerInfiniteScrollProps) => {
  const layoutRef = useRef<PanelsLayout>([]);
  const searchLayoutRef = useRef<PanelsLayout>([]);
  const [shownLayout, setShownLayout] = useState<PanelsLayout>([]);
  const [hasMorePanelsToRender, setHasMorePanelsToRender] = useState(true);

  const loadMorePanels = useCallback(() => {
    const currentLength = shownLayout.length;

    const currentLayout = isSearchMode
      ? searchLayoutRef.current
      : layoutRef.current;

    const nextLayout = currentLayout.slice(
      0,
      currentLength + NUMBER_RENDERED_PANELS_LIMIT
    );

    if (currentLength < currentLayout.length) {
      setShownLayout(nextLayout);
    } else {
      setHasMorePanelsToRender(false);
    }
  }, [shownLayout.length, isSearchMode]);

  useEffect(() => {
    setHasMorePanelsToRender(true);
    setShownLayout(previousShownLayout => {
      const isRenderFromTop = isEmpty(layoutRef.current);
      const previousShownLength = previousShownLayout.length;
      const layoutHasGrown = layout.length > previousShownLength;

      if (isRenderFromTop) {
        return layout.slice(0, NUMBER_RENDERED_PANELS_LIMIT);
      }

      if (layoutHasGrown) {
        const newPanelsToRender = Math.min(
          previousShownLength + NUMBER_RENDERED_PANELS_LIMIT,
          layout.length
        );
        return layout.slice(0, newPanelsToRender);
      }

      return layout.slice(0, previousShownLength);
    });

    layoutRef.current = layout;
  }, [layout]);

  const configIds = useMemo(() => configs?.map(c => c.chartId) || [], [
    configs
  ]);

  // when during search mode, a user changes the view (fe, pagination)
  useDeepCompareEffect(() => {
    if (isSearchMode) {
      searchLayoutRef.current = initializeLayout({
        panels: configs,
        initialLayout: [],
        entireRowCharts
      });
      setShownLayout(
        searchLayoutRef.current.slice?.(0, NUMBER_RENDERED_PANELS_LIMIT)
      );

      setHasMorePanelsToRender(true);
    }
  }, [configIds]);

  // when search mode has been changed
  useEffect(() => {
    if (!isSearchMode) {
      searchLayoutRef.current = [];
      setShownLayout(
        layoutRef.current.slice?.(0, NUMBER_RENDERED_PANELS_LIMIT)
      );
      setHasMorePanelsToRender(true);
    }
  }, [isSearchMode]);

  return {
    hasMorePanelsToRender,
    shownLayout,
    loadMorePanels
  };
};

export default useBatchChartContainerInfiniteScroll;
